import { useState, useRef, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { 
  FileText, 
  ChevronDown,
  Printer,
  Eye,
  Clock,
  User
} from 'lucide-react'

interface RecentOrdersDropdownProps {
  recentOrders: any[] // API sales data structure
  onReprintReceipt: (saleId: string) => void
  onViewOrder: (saleId: string) => void
  formatCurrency: (amount: number) => string
  compact?: boolean
}

export const RecentOrdersDropdown = ({
  recentOrders,
  onReprintReceipt,
  onViewOrder,
  formatCurrency,
  compact = false
}: RecentOrdersDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now()
    const diff = now - timestamp
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (days > 0) return `${days}d ago`
    if (hours > 0) return `${hours}h ago`
    if (minutes > 0) return `${minutes}m ago`
    return 'Just now'
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1 h-8 text-xs ${compact ? 'w-8 p-0 relative' : 'gap-2'}`}
        title={compact ? "Recent Orders" : undefined}
      >
        <FileText className="h-4 w-4" />
        {!compact && "Recent Orders"}
        {recentOrders.length > 0 && (
          <Badge variant="secondary" className={`h-4 text-xs ${compact ? 'px-1 absolute -top-1 -right-1' : 'px-1'}`}>
            {recentOrders.length}
          </Badge>
        )}
        {!compact && <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-10 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-96 overflow-hidden">
          <div className="p-3 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm">Recent Orders</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Last 10 orders for reprinting</p>
          </div>
          
          <div className="overflow-y-auto max-h-80">
            {recentOrders.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No recent orders</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {recentOrders.map((order) => (
                  <div key={order.id} className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-sm text-gray-900 dark:text-gray-100">
                            #{order.bill?.bill_number || order.bill_number || order.invoice_number || order.id.slice(0, 8)}
                          </p>
                          <Badge variant="outline" size="sm" className="capitalize">
                            {order.payment_method}
                          </Badge>
                        </div>
                        
                        {order.customer && (
                          <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 mb-1">
                            <User className="w-3 h-3" />
                            {order.customer.name}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTimeAgo(new Date(order.created_at).getTime())}
                          </span>
                          <span>{order.items?.length || 0} items</span>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(order.total_amount)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          onReprintReceipt(order.id)
                          setIsOpen(false)
                        }}
                        className="flex items-center gap-1 h-6 px-2 text-xs"
                      >
                        <Printer className="w-3 h-3" />
                        Reprint
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          onViewOrder(order.id)
                          setIsOpen(false)
                        }}
                        className="flex items-center gap-1 h-6 px-2 text-xs"
                      >
                        <Eye className="w-3 h-3" />
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}